import { observable, action, computed, makeObservable } from 'mobx';

import ProgressBarStep, {
  ProgressBarDefaults,
} from '../interfaces/ProgressBarStep';
import { Nationality } from '../interfaces/Nationality';
import PersonalDataForm from '../interfaces/PersonalDataForm';
import Language from '../enums/Language';
import Steps from '../enums/Steps';
import HealthDeclarationStatus from '../enums/HealthDeclarationStatus';
import UnderwriterOwner from '../enums/UnderwriterOwner';
import ProductType from '../enums/ProductType';
import { Answer } from '../interfaces/Underwriter/AnswersRequest';
import NavigationMenuCategory from '../interfaces/Underwriter/NavigationMenuCategory';
import { FormValueRadio } from '../interfaces/Underwriter/FormValue';
import GeneralError from '../enums/GeneralError';
import HealthDocFile from '../interfaces/HealthDocFile';
import { Occupation } from '../interfaces/Occupation';
import PolicyTerms from '../enums/PolicyTerms';
import { Option } from '@taf-ui/core';
import { SelectOptions } from '../types/SelectOptions';
import { ProductSpecificData } from '../interfaces/ProductConfig';

type ObservationKeys =
  | 'locale'
  | 'isInstructionsAccepted'
  | 'progressBar'
  | 'nationalities'
  | 'occupations'
  | 'token'
  | 'personalDataFormState'
  | 'isPersonalDataFormValid'
  | 'isAgreedWithProcessingPersonalInfo'
  | 'isAgreedWithPrivacyInfo'
  | 'isAgreedWithProcessingMedicalInfo'
  | 'isMobilePhoneCorrect'
  | 'isConsent'
  | 'isAgreeWithPrivacyStatement'
  | 'finalComment'
  | 'signUrl'
  | 'interviewDisplayComponent'
  | 'isMagnumCaseCompleted'
  | 'isProgressBarHidden'
  | 'underwriterOwner'
  | 'hasRemarks'
  | 'generalError'
  | 'isHealthDeclarationDocuments'
  | 'healthDeclarationFileList'
  | 'isHealthDeclarationFileListLoaded'
  | 'isAllDocumentsValid'
  | 'productSpecificInstructionsPage'
  | 'productSpecificProgressBarLabels'
  | 'productSpecificInfo'
  | 'isWefoxInfoCompleteAndCorrect'
  | 'isWefoxConsequencesOfViolation'
  | 'isWefoxDataProcessingAndConfidentiality'
  | 'isWefoxDataProtection'
  | 'isWefoxHealthDecProvisions'
  | 'isWefoxInformHealthChanges'
  | 'productSpecificDocumentsInstructions';

class AppStorage {
  private static instance: AppStorage | null;

  private locale: Language;

  private isInstructionsAccepted: boolean;

  private progressBar: Array<ProgressBarStep>;

  private nationalities: Array<Nationality>;

  private occupations: Array<Occupation>;

  private token: string;

  private personalDataFormState: Partial<PersonalDataForm>;

  private isPersonalDataFormValid: boolean;

  private isAgreedWithProcessingPersonalInfo: boolean;

  private isAgreedWithPrivacyInfo: boolean;

  private isAgreedWithProcessingMedicalInfo: boolean;

  private isMobilePhoneCorrect: boolean;

  private isConsent: boolean;

  private isAgreeWithPrivacyStatement: boolean;

  private isWefoxInfoCompleteAndCorrect: boolean;

  private isWefoxConsequencesOfViolation: boolean;

  private isWefoxDataProcessingAndConfidentiality: boolean;

  private isWefoxDataProtection: boolean;

  private isWefoxHealthDecProvisions: boolean;

  private isWefoxInformHealthChanges: boolean;

  private finalComment: string;

  private signUrl: string;

  private interviewDisplayComponent: HTMLMagnumInterviewDisplayComponentElement | null;

  private isMagnumCaseCompleted: boolean;

  private isProgressBarHidden: boolean;

  private underwriterOwner: UnderwriterOwner | null;

  private hasRemarks: FormValueRadio;

  private generalError: GeneralError;

  private isHealthDeclarationDocuments: boolean;

  private healthDeclarationFileList: Array<HealthDocFile>;

  private isHealthDeclarationFileListLoaded: boolean;

  private isAllDocumentsValid: boolean;

  private isMagnumSourcesLoaded: boolean;

  private currentHDStatus: HealthDeclarationStatus | null;

  private productType: ProductType;

  private policyTerms: PolicyTerms;

  private storedTafQuestionnaireAnswers: Record<string, Answer>;

  private tafQuestionnaireMenuState: Array<NavigationMenuCategory>;

  private isAllTAFQuestionnaireFormValidFromBEValidation: boolean;

  private isPregnancyQuestionForMaleSaved: boolean;

  private isDefaultOccupationQuestionSaved: boolean;

  private productSpecificInstructionsPage: null | string;

  private productSpecificProgressBarLabels: string[];

  private productSpecificInfo: ProductSpecificData;

  private productSpecificDocumentsInstructions: undefined | string;

  private deductPremiumFromTaxes: boolean;

  private constructor() {
    this.locale = Language.Dutch;
    this.isInstructionsAccepted = false;
    this.isMagnumSourcesLoaded = false;
    this.progressBar = ProgressBarDefaults;
    this.nationalities = [];
    this.occupations = [];
    this.personalDataFormState = {};
    this.isPersonalDataFormValid = false;
    this.token = '';
    /*
     * Final step. TAF checkboxes
     * */
    this.isAgreedWithProcessingPersonalInfo = false;
    this.isAgreedWithProcessingMedicalInfo = false;
    this.isMobilePhoneCorrect = false;
    this.isConsent = false;
    this.isAgreeWithPrivacyStatement = false;
    /*
     * Final step. wefox checkboxes
     * */
    this.isWefoxInfoCompleteAndCorrect = false;
    this.isWefoxConsequencesOfViolation = false;
    this.isWefoxDataProcessingAndConfidentiality = false;
    this.isWefoxDataProtection = false;
    this.isWefoxHealthDecProvisions = false;
    this.isWefoxInformHealthChanges = false;

    /*
     * Final step dehoop checkbox
     * */

    this.isAgreedWithPrivacyInfo = false;

    this.finalComment = '';
    this.signUrl = '';
    this.interviewDisplayComponent = null;
    this.isMagnumCaseCompleted = false;
    this.currentHDStatus = null;
    this.underwriterOwner = null;
    this.productType = ProductType.UNKNOWN;
    this.policyTerms = PolicyTerms.UNKNOWN;
    this.storedTafQuestionnaireAnswers = {};
    this.tafQuestionnaireMenuState = [];
    this.isAllTAFQuestionnaireFormValidFromBEValidation = false;
    this.isPregnancyQuestionForMaleSaved = false;
    this.isProgressBarHidden = true;
    this.hasRemarks = 'NO';
    this.generalError = null;
    this.isHealthDeclarationDocuments = false;
    this.healthDeclarationFileList = [];
    this.isHealthDeclarationFileListLoaded = false;
    this.isDefaultOccupationQuestionSaved = false;
    this.isAllDocumentsValid = false;
    this.productSpecificInstructionsPage = null;
    this.productSpecificProgressBarLabels = [];
    this.productSpecificInfo = {
      infoEmail: '',
      applicationFormDownloadLink: '',
      insurersLink: '',
      privacyStatementLink: '',
      companyName: '',
      telephoneNumber: '',
      telephoneNumberHref: '',
    };
    this.productSpecificDocumentsInstructions = undefined;
    this.deductPremiumFromTaxes = false;

    makeObservable<AppStorage, ObservationKeys>(this, {
      locale: observable,
      isInstructionsAccepted: observable,
      progressBar: observable,
      nationalities: observable,
      occupations: observable,
      token: observable,
      personalDataFormState: observable,
      isPersonalDataFormValid: observable,
      /* Taf checkboxes */
      isAgreedWithProcessingPersonalInfo: observable,
      isAgreedWithProcessingMedicalInfo: observable,
      isMobilePhoneCorrect: observable,
      isConsent: observable,
      isAgreeWithPrivacyStatement: observable,
      /* wefox checkboxes */
      isWefoxInfoCompleteAndCorrect: observable,
      isWefoxConsequencesOfViolation: observable,
      isWefoxDataProcessingAndConfidentiality: observable,
      isWefoxDataProtection: observable,
      isWefoxHealthDecProvisions: observable,
      isWefoxInformHealthChanges: observable,

      /* dehoop checkbox */
      isAgreedWithPrivacyInfo: observable,

      finalComment: observable,
      signUrl: observable,
      interviewDisplayComponent: observable,
      isMagnumCaseCompleted: observable,
      isProgressBarHidden: observable,
      underwriterOwner: observable,
      hasRemarks: observable,
      generalError: observable,
      isHealthDeclarationDocuments: observable,
      healthDeclarationFileList: observable,
      isHealthDeclarationFileListLoaded: observable,
      isAllDocumentsValid: observable,
      productSpecificInstructionsPage: observable,
      setProductSpecificInstructionsPage: action(
        'Set product specific Instructions Page',
      ),
      productSpecificDocumentsInstructions: observable,
      setProductSpecificDocumentsInstructions: action(
        'Set product specific documents instructions',
      ),
      productSpecificProgressBarLabels: observable,
      setProductSpecificProgressBarLabels: action(
        'Set product specific Progress Bar Labels',
      ),
      getProductSpecificProgressBarLabels: computed,
      productSpecificInfo: observable,
      setProductSpecificInfo: action('Set product specific info'),
      getProductSpecificInfo: computed,
      setGeneralError: action('Set general error'),
      getGeneralError: computed,
      setHasRemarks: action('Set hasRemarks field'),
      getHasRemarks: computed,
      setIsProgressBarHidden: action('Set isProgressBarHidden'),
      getIsProgressBarHidden: computed,
      setIsMagnumCaseCompleted: action('Set isMagnumCase completed'),
      getIsMagnumCaseCompleted: computed,
      setSignUrl: action('Set sign url'),
      getSignUrl: computed,
      setFinalComment: action('Set final comment'),
      getFinalComment: computed,
      /* Taf checkboxes */
      setIsMobilePhoneCorrect: action('Set is mobile phone correct'),
      getIsMobilePhoneCorrect: computed,
      setIsAgreedWithProcessingMedicalInfo: action(
        'Set is agreed with processing medical info',
      ),
      getIsAgreedWithProcessingMedicalInfo: computed,
      setIsAgreedWithProcessingPersonalInfo: action(
        'Set is agreed with processing personal info',
      ),
      getIsAgreedWithProcessingPersonalInfo: computed,
      /* wefox checkboxes */
      setIsWefoxInfoCompleteAndCorrect: action(
        'Set is wefox info complete and correct',
      ),
      getIsWefoxInfoCompleteAndCorrect: computed,
      setIsWefoxConsequencesOfViolation: action(
        'Set is wefox consequences of violation',
      ),
      getIsWefoxConsequencesOfViolation: computed,
      setIsWefoxDataProcessingAndConfidentiality: action(
        'Set is wefox data processing and confidentiality',
      ),
      getIsWefoxDataProcessingAndConfidentiality: computed,
      setIsWefoxDataProtection: action('Set is wefox data protection'),
      getIsWefoxDataProtection: computed,
      setIsWefoxHealthDecProvisions: action(
        'Set is wefox health declaration provisions',
      ),
      getIsWefoxHealthDecProvisions: computed,
      setIsWefoxInformHealthChanges: action(
        'Set is wefox inform health changes',
      ),
      getIsWefoxInformHealthChanges: computed,

      setIsPersonalDataFormValid: action('Set is personal data form valid'),
      getIsPersonalDataFormValid: computed,
      setPersonalDataFormState: action('Set personal data form state'),
      getPersonalDataFormState: computed,
      setToken: action('Set token'),
      getToken: computed,
      getNationalities: computed,
      getNationalitiesOptions: computed,
      setNationalities: action('Set nationalities'),
      getOccupations: computed,
      getOccupationsOptions: computed,
      setOccupations: action('Set nationalities'),
      getProgressBar: computed,
      getActiveStep: computed,
      setProgressBar: action('Set progress bar'),
      setIsInstructionsAccepted: action('Set isInstructionsAccepted field'),
      getIsInstructionsAccepted: computed,
      getLocale: computed,
      setLocale: action('Set language'),
      setUnderwriterOwner: action('Set underwriterOwner'),
      getUnderwriterOwner: computed,
      setIsHealthDeclarationDocuments: action(
        'Set isHealthDeclarationDocuments',
      ),
      getIsHealthDeclarationDocuments: computed,
      setHealthDeclarationFileList: action('Set healthDeclarationFileList'),
      getHealthDeclarationFileList: computed,
      getHasValidDocumentsToBeUploaded: computed,
      getAtLeastOneUploaded: computed,
      setCurrentDocumentsAsLoaded: action('Set current documents as loaded'),
      setIsAllDocumentsValid: action('Set isAllDocumentsValid'),
      getIsAllDocumentsValid: computed,
      setFieldsWhenReopenHD: action('Reopen HD'),
      modifyProgressBar: action('Modify progress bar'),
    });
  }

  static getInstance(): AppStorage {
    if (AppStorage.instance) {
      return AppStorage.instance;
    }

    AppStorage.instance = new AppStorage();
    return AppStorage.instance;
  }

  setProductSpecificInfo = (data: ProductSpecificData) => {
    this.productSpecificInfo = data;
  };

  get getProductSpecificInfo(): ProductSpecificData {
    return this.productSpecificInfo;
  }

  setProductSpecificProgressBarLabels = (value: string[]): void => {
    this.productSpecificProgressBarLabels = value;
  };

  get getProductSpecificProgressBarLabels(): null | string[] {
    if (
      this.underwriterOwner === UnderwriterOwner.IPT_MAGNUM ||
      this.underwriterOwner === UnderwriterOwner.QL_REFLEX ||
      this.underwriterOwner === UnderwriterOwner.ZEL_AURA
    ) {
      return this.productSpecificProgressBarLabels.filter(
        (label, index) => index !== 3,
      );
    }

    return this.productSpecificProgressBarLabels;
  }

  setProductSpecificInstructionsPage = (value: string): void => {
    this.productSpecificInstructionsPage = value;
  };

  getProductSpecificInstructionsPage = (): null | string => {
    return this.productSpecificInstructionsPage;
  };

  setProductSpecificDocumentsInstructions = (
    value: string | undefined,
  ): void => {
    this.productSpecificDocumentsInstructions = value;
  };

  getProductSpecificDocumentsInstructions = (): undefined | string => {
    return this.productSpecificDocumentsInstructions;
  };

  setIsPregnancyQuestionForMaleSaved = (value: boolean): void => {
    this.isPregnancyQuestionForMaleSaved = value;
  };

  getIsPregnancyQuestionForMaleSaved = (): boolean =>
    this.isPregnancyQuestionForMaleSaved;

  setIsDefaultOccupationQuestionSaved = (value: boolean): void => {
    this.isDefaultOccupationQuestionSaved = value;
  };

  getIsDefaultOccupationQuestionSaved = (): boolean =>
    this.isDefaultOccupationQuestionSaved;

  setIsAllTAFQuestionnaireFormValidFromBEValidation = (
    isAllTAFQuestionnaireFormValidFromBEValidation: boolean,
  ): void => {
    this.isAllTAFQuestionnaireFormValidFromBEValidation =
      isAllTAFQuestionnaireFormValidFromBEValidation;
  };

  getIsAllTAFQuestionnaireFormValidFromBEValidation = (): boolean =>
    this.isAllTAFQuestionnaireFormValidFromBEValidation;

  setTafQuestionnaireMenuState = (
    menu: Array<NavigationMenuCategory>,
  ): void => {
    this.tafQuestionnaireMenuState = menu;
  };

  getTafQuestionnaireMenuState = (): Array<NavigationMenuCategory> =>
    this.tafQuestionnaireMenuState;

  storeAnswer = (answer: Answer): void => {
    this.storedTafQuestionnaireAnswers[answer.questionPathId] = answer;
  };

  getStoredAnswers = (): Record<string, Answer> =>
    this.storedTafQuestionnaireAnswers;

  resetAnswers = (): void => {
    this.storedTafQuestionnaireAnswers = {};
  };

  setGeneralError = (error: GeneralError): void => {
    this.generalError = error;
  };

  get getGeneralError(): GeneralError {
    return this.generalError;
  }

  setHasRemarks = (hasRemarks: FormValueRadio): void => {
    this.hasRemarks = hasRemarks;
  };

  get getHasRemarks(): FormValueRadio {
    return this.hasRemarks;
  }

  setIsProgressBarHidden = (isProgressBarHidden: boolean): void => {
    this.isProgressBarHidden = isProgressBarHidden;
  };

  get getIsProgressBarHidden(): boolean {
    return this.isProgressBarHidden;
  }

  setIsMagnumCaseCompleted = (isMagnumCaseCompleted: boolean): void => {
    this.isMagnumCaseCompleted = isMagnumCaseCompleted;
  };

  get getIsMagnumCaseCompleted(): boolean {
    return this.isMagnumCaseCompleted;
  }

  setSignUrl = (signUrl: string): void => {
    this.signUrl = signUrl;
  };

  get getSignUrl(): string {
    return this.signUrl;
  }

  setFinalComment = (finalComment: string): void => {
    this.finalComment = finalComment;
  };

  get getFinalComment(): string {
    return this.finalComment;
  }

  setIsMobilePhoneCorrect = (isMobilePhoneCorrect: boolean): void => {
    this.isMobilePhoneCorrect = isMobilePhoneCorrect;
  };

  setIsConsent = (isConsent: boolean): void => {
    this.isConsent = isConsent;
  };

  setIsAgreeWithPrivacyStatement = (
    isAgreeWithPrivacyStatement: boolean,
  ): void => {
    this.isAgreeWithPrivacyStatement = isAgreeWithPrivacyStatement;
  };

  get getIsMobilePhoneCorrect(): boolean {
    return this.isMobilePhoneCorrect;
  }

  get getIsConsent(): boolean {
    return this.isConsent;
  }

  get getIsAgreeWithPrivacyStatement(): boolean {
    return this.isAgreeWithPrivacyStatement;
  }

  setIsAgreedWithProcessingMedicalInfo = (
    isAgreedWithProcessingMedicalInfo: boolean,
  ): void => {
    this.isAgreedWithProcessingMedicalInfo = isAgreedWithProcessingMedicalInfo;
  };

  get getIsAgreedWithProcessingMedicalInfo(): boolean {
    return this.isAgreedWithProcessingMedicalInfo;
  }

  setIsAgreedWithProcessingPersonalInfo = (
    isAgreedWithProcessingPersonalInfo: boolean,
  ): void => {
    this.isAgreedWithProcessingPersonalInfo =
      isAgreedWithProcessingPersonalInfo;
  };

  get getIsAgreedWithProcessingPersonalInfo(): boolean {
    return this.isAgreedWithProcessingPersonalInfo;
  }

  setIsAgreedWithPrivacyInfo = (isAgreedWithPrivacyInfo: boolean): void => {
    this.isAgreedWithPrivacyInfo = isAgreedWithPrivacyInfo;
  };

  get getIsAgreedWithPrivacyInfo(): boolean {
    return this.isAgreedWithPrivacyInfo;
  }

  setIsWefoxInfoCompleteAndCorrect = (
    isWefoxInfoCompleteAndCorrect: boolean,
  ): void => {
    this.isWefoxInfoCompleteAndCorrect = isWefoxInfoCompleteAndCorrect;
  };

  get getIsWefoxInfoCompleteAndCorrect(): boolean {
    return this.isWefoxInfoCompleteAndCorrect;
  }

  setIsWefoxConsequencesOfViolation = (
    isWefoxConsequencesOfViolation: boolean,
  ): void => {
    this.isWefoxConsequencesOfViolation = isWefoxConsequencesOfViolation;
  };

  get getIsWefoxConsequencesOfViolation(): boolean {
    return this.isWefoxConsequencesOfViolation;
  }

  setIsWefoxDataProcessingAndConfidentiality = (
    isWefoxDataProcessingAndConfidentiality: boolean,
  ): void => {
    this.isWefoxDataProcessingAndConfidentiality =
      isWefoxDataProcessingAndConfidentiality;
  };

  get getIsWefoxDataProcessingAndConfidentiality(): boolean {
    return this.isWefoxDataProcessingAndConfidentiality;
  }

  setIsWefoxDataProtection = (isWefoxDataProtection: boolean): void => {
    this.isWefoxDataProtection = isWefoxDataProtection;
  };

  get getIsWefoxDataProtection(): boolean {
    return this.isWefoxDataProtection;
  }

  setIsWefoxHealthDecProvisions = (
    isWefoxHealthDecProvisions: boolean,
  ): void => {
    this.isWefoxHealthDecProvisions = isWefoxHealthDecProvisions;
  };

  get getIsWefoxHealthDecProvisions(): boolean {
    return this.isWefoxHealthDecProvisions;
  }

  setIsWefoxInformHealthChanges = (
    isWefoxInformHealthChanges: boolean,
  ): void => {
    this.isWefoxInformHealthChanges = isWefoxInformHealthChanges;
  };

  get getIsWefoxInformHealthChanges(): boolean {
    return this.isWefoxInformHealthChanges;
  }

  setIsPersonalDataFormValid = (isPersonalDataFormValid: boolean): void => {
    this.isPersonalDataFormValid = isPersonalDataFormValid;
  };

  get getIsPersonalDataFormValid(): boolean {
    return this.isPersonalDataFormValid;
  }

  setPersonalDataFormState = (
    personalDataFormState: Partial<PersonalDataForm>,
  ): void => {
    this.personalDataFormState = personalDataFormState;
  };

  get getPersonalDataFormState(): Partial<PersonalDataForm> {
    return this.personalDataFormState;
  }

  setToken = (token: string): void => {
    this.token = token;
  };

  get getToken(): string {
    return this.token;
  }

  get getNationalities(): Array<Nationality> {
    return this.nationalities;
  }

  get getNationalitiesOptions(): Array<Option> {
    return this.nationalities.map((nationality) => ({
      value: nationality.countryCode,
      title: nationality.displayName,
    }));
  }

  setNationalities = (nationalities: Array<Nationality>): void => {
    this.nationalities = nationalities;
  };

  get getOccupations(): Array<Occupation> {
    return this.occupations;
  }

  get getOccupationsOptions(): Array<SelectOptions> {
    return this.occupations.map((occupation) => ({
      value: occupation.occupation.name,
      label: occupation.occupation.name,
    }));
  }

  setOccupations = (occupations: Array<Occupation>): void => {
    this.occupations = occupations;
  };

  get getProgressBar(): Array<ProgressBarStep> {
    return this.progressBar;
  }

  getProgressBarByUnderwriterOwner(): Array<ProgressBarStep> {
    if (
      this.underwriterOwner === UnderwriterOwner.IPT_MAGNUM ||
      this.underwriterOwner === UnderwriterOwner.QL_REFLEX ||
      this.underwriterOwner === UnderwriterOwner.ZEL_AURA
    ) {
      return this.progressBar.filter(
        (progressBarStep) => progressBarStep.step !== 4,
      );
    }

    return this.progressBar;
  }

  get getActiveStep(): ProgressBarStep {
    return this.progressBar.filter((step) => step.active)[0];
  }

  setProgressBar = (progressBar: Array<ProgressBarStep>): void => {
    this.progressBar = progressBar;
  };

  getIsMagnumSourcesLoaded = (): boolean => this.isMagnumSourcesLoaded;

  setIsMagnumSourcesLoaded = (key: boolean): void => {
    this.isMagnumSourcesLoaded = key;
  };

  setIsInstructionsAccepted = (key: boolean): void => {
    this.isInstructionsAccepted = key;
  };

  get getIsInstructionsAccepted(): boolean {
    return this.isInstructionsAccepted;
  }

  get getLocale(): Language {
    return this.locale;
  }

  setLocale = (lang: Language): void => {
    this.locale = lang;
  };

  getProductType = (): ProductType => this.productType;

  setProductType = (productType: ProductType): void => {
    this.productType = productType;
  };

  getPolicyTerms = (): PolicyTerms => this.policyTerms;

  setPolicyTerms = (policyTerms: PolicyTerms): void => {
    this.policyTerms = policyTerms;
  };

  setUnderwriterOwner = (underwriterOwner: UnderwriterOwner): void => {
    this.underwriterOwner = underwriterOwner;
  };

  get getUnderwriterOwner(): UnderwriterOwner | null {
    return this.underwriterOwner;
  }

  setIsHealthDeclarationDocuments = (
    isHealthDeclarationDocuments: boolean,
  ): void => {
    this.isHealthDeclarationDocuments = isHealthDeclarationDocuments;
  };

  get getIsHealthDeclarationDocuments(): boolean {
    return this.isHealthDeclarationDocuments;
  }

  setHealthDeclarationFileList = (
    healthDeclarationFileList: Array<HealthDocFile>,
  ): void => {
    this.healthDeclarationFileList = healthDeclarationFileList;
  };

  get getHealthDeclarationFileList(): Array<HealthDocFile> {
    return this.healthDeclarationFileList;
  }

  get getHasValidDocumentsToBeUploaded(): boolean {
    return this.healthDeclarationFileList.some(
      (file) => !file.isLoaded && file.isValid,
    );
  }

  get getAtLeastOneUploaded(): boolean {
    return this.healthDeclarationFileList.some((file) => file.isLoaded);
  }

  setCurrentDocumentsAsLoaded = (): void => {
    this.healthDeclarationFileList = this.healthDeclarationFileList.map(
      (doc) => {
        doc.isLoaded = true;
        return doc;
      },
    );
  };

  setIsAllDocumentsValid = (isAllDocumentsValid: boolean): void => {
    this.isAllDocumentsValid = isAllDocumentsValid;
  };

  get getIsAllDocumentsValid(): boolean {
    return this.isAllDocumentsValid;
  }

  getCurrentHDStatus = (): HealthDeclarationStatus | null =>
    this.currentHDStatus;

  setCurrentHDStatus = (status: HealthDeclarationStatus): void => {
    this.currentHDStatus = status;
  };

  isDeductPremiumFromTaxes(): boolean {
    return this.deductPremiumFromTaxes;
  }

  setDeductPremiumFromTaxes(value: boolean) {
    this.deductPremiumFromTaxes = value;
  }

  modifyProgressBar = (
    stepNumber: Steps,
    fieldName: 'active' | 'valid' | 'done',
    value: boolean,
  ): void => {
    this.progressBar = this.progressBar.map((item) => {
      if (stepNumber === 'all') {
        item[fieldName] = value;
      } else if (item.step === stepNumber) {
        item[fieldName] = value;
      }
      return item;
    });
  };

  getDataForDraft = (): Partial<Storage> => ({
    /* locale: this.locale, */
    isInstructionsAccepted: this.isInstructionsAccepted,
    progressBar: this.progressBar,
    personalDataFormState: this.personalDataFormState,
    isPersonalDataFormValid: this.isPersonalDataFormValid,
    /* TAF checkboxes */
    isAgreedWithProcessingPersonalInfo: this.isAgreedWithProcessingPersonalInfo,
    isAgreedWithProcessingMedicalInfo: this.isAgreedWithProcessingMedicalInfo,
    /* wefox checkboxes */
    isWefoxInfoCompleteAndCorrect: this.isWefoxInfoCompleteAndCorrect,
    isWefoxConsequencesOfViolation: this.isWefoxConsequencesOfViolation,
    isWefoxDataProcessingAndConfidentiality:
      this.isWefoxDataProcessingAndConfidentiality,
    isWefoxDataProtection: this.isWefoxDataProtection,
    isWefoxHealthDecProvisions: this.isWefoxHealthDecProvisions,
    isWefoxInformHealthChanges: this.isWefoxInformHealthChanges,

    /* dehoop checkbox*/
    isAgreedWithPrivacyInfo: this.isAgreedWithPrivacyInfo,

    isMobilePhoneCorrect: this.isMobilePhoneCorrect,
    isConsent: this.isConsent,
    isAgreeWithPrivacyStatement: this.isAgreeWithPrivacyStatement,
    finalComment: this.finalComment,
    underwriterOwner: this.underwriterOwner,
    currentHDStatus: this.currentHDStatus,
    productType: this.productType,
    tafQuestionnaireMenuState: this.tafQuestionnaireMenuState,
    isAllTAFQuestionnaireFormValidFromBEValidation:
      this.isAllTAFQuestionnaireFormValidFromBEValidation,
    hasRemarks: this.hasRemarks,
    isHealthDeclarationDocuments: this.isHealthDeclarationDocuments,
    policyTerms: this.policyTerms,
    deductPremiumFromTaxes: this.deductPremiumFromTaxes,
  });

  setDataFromDraft = ({
    /* locale, */
    isInstructionsAccepted,
    progressBar,
    personalDataFormState,
    isPersonalDataFormValid,
    isAgreedWithProcessingPersonalInfo,
    isAgreedWithPrivacyInfo,
    isAgreedWithProcessingMedicalInfo,
    isMobilePhoneCorrect,
    isAgreeOfOutcome,
    isAgreeWithPrivacyStatement: isAgreeWithPrivacyStatement,
    finalComment,
    underwriterOwner,
    currentHDStatus,
    productType,
    tafQuestionnaireMenuState,
    isAllTAFQuestionnaireFormValidFromBEValidation,
    hasRemarks,
    isHealthDeclarationDocuments,
    policyTerms,
    isWefoxInfoCompleteAndCorrect,
    isWefoxConsequencesOfViolation,
    isWefoxDataProcessingAndConfidentiality,
    isWefoxDataProtection,
    isWefoxHealthDecProvisions,
    isWefoxInformHealthChanges,
    deductPremiumFromTaxes,
  }: Partial<Storage>): void => {
    /* this.locale = locale; */
    this.setIsInstructionsAccepted(isInstructionsAccepted as boolean);
    this.setProgressBar(progressBar as Array<ProgressBarStep>);
    this.setPersonalDataFormState(
      personalDataFormState as Partial<PersonalDataForm>,
    );
    this.setIsPersonalDataFormValid(isPersonalDataFormValid as boolean);
    /* TAF checkboxes */
    this.setIsAgreedWithProcessingPersonalInfo(
      isAgreedWithProcessingPersonalInfo as boolean,
    );
    this.setIsAgreedWithProcessingMedicalInfo(
      (isAgreedWithProcessingMedicalInfo as boolean) || false,
    );
    /* wefox checkboxes */
    this.setIsWefoxInfoCompleteAndCorrect(
      isWefoxInfoCompleteAndCorrect as boolean,
    );
    this.setIsWefoxConsequencesOfViolation(
      isWefoxConsequencesOfViolation as boolean,
    );
    this.setIsWefoxDataProcessingAndConfidentiality(
      isWefoxDataProcessingAndConfidentiality as boolean,
    );
    this.setIsWefoxDataProtection(isWefoxDataProtection as boolean);
    this.setIsWefoxHealthDecProvisions(isWefoxHealthDecProvisions as boolean);
    this.setIsWefoxInformHealthChanges(isWefoxInformHealthChanges as boolean);
    /* dehoop checkbox */
    this.setIsAgreedWithPrivacyInfo(
      (isAgreedWithPrivacyInfo as boolean) || false,
    );

    this.setIsMobilePhoneCorrect((isMobilePhoneCorrect as boolean) || false);
    this.setIsConsent((isAgreeOfOutcome as boolean) || false);
    this.setIsAgreeWithPrivacyStatement(
      (isAgreeWithPrivacyStatement as boolean) || false,
    );
    this.setFinalComment(finalComment as string);
    this.setUnderwriterOwner(underwriterOwner as UnderwriterOwner);
    this.setCurrentHDStatus(currentHDStatus as HealthDeclarationStatus);
    this.setProductType(productType as ProductType);
    this.setTafQuestionnaireMenuState(
      tafQuestionnaireMenuState as Array<NavigationMenuCategory>,
    );
    this.setIsAllTAFQuestionnaireFormValidFromBEValidation(
      isAllTAFQuestionnaireFormValidFromBEValidation as boolean,
    );
    this.setHasRemarks(hasRemarks as FormValueRadio);
    this.setIsHealthDeclarationDocuments(
      isHealthDeclarationDocuments as boolean,
    );
    this.setPolicyTerms(policyTerms as PolicyTerms);
    this.setDeductPremiumFromTaxes(deductPremiumFromTaxes as boolean);
  };

  setFieldsWhenReopenHD = (): void => {
    this.setIsInstructionsAccepted(true);
    this.setIsAgreedWithProcessingPersonalInfo(true);
    this.setIsAgreedWithProcessingMedicalInfo(true);
    this.setIsAgreedWithPrivacyInfo(true);
    this.setIsMobilePhoneCorrect(true);
    this.setIsAllTAFQuestionnaireFormValidFromBEValidation(true);
    this.modifyProgressBar(Steps.All, 'valid', true);
    this.modifyProgressBar(Steps.All, 'done', true);
  };

  reset = (): void => {
    AppStorage.instance = null;
  };
}

export default AppStorage;
